/**
 * OfficeCliRender — the shared officecli HTML render surface (#620).
 *
 * Extracted from OfficeVersionViewer so BOTH the version-history preview and the
 * live-editor read-only View mode render through one path. Fetches the
 * self-contained HTML from `/api/files/:id/officecli-preview` and shows it in a
 * sandboxed iframe; on non-OOXML / render failure it renders the caller's `fallback`
 * (WOPI viewer for history, Collabora read-only for live).
 *
 * `versionId` omitted = the CURRENT HEAD (the endpoint's no-version branch, B1).
 *
 * SECURITY (#620 Phase C): the iframe is OPAQUE-ORIGIN — `sandbox="allow-scripts"`
 * with NO `allow-same-origin` — so a script that runs inside it (e.g. from an
 * unescaped cell) runs in a throwaway null origin and cannot reach the SPA origin,
 * the session cookie, or any other tenant's data. Because the parent can no longer
 * read the frame's DOM, everything it used to reach in for is BAKED into the srcDoc
 * (theme, CSP, saved view state via buildOfficeCliSrcDoc) and view-state changes come
 * back via a `postMessage` the parent accepts ONLY from this iframe's own window.
 *
 * View sync is keyed `${fileId}:${mode}` via viewSync.ts (B4) — one scheme for both
 * officecli surfaces. The WOPI/Collabora path keeps its own bare-`fileId`
 * `__papanViewSync` store; the two view-state shapes don't map, so they are NOT bridged.
 */
import { useEffect, useState, useRef, useMemo, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { fetchRaw } from '@papan/api-client'
import { useThemeOptional } from '@papan/core-ui/ui'
import { buildOfficeCliSrcDoc, type OfficeCliViewState } from './officecli-srcdoc.js'
import { viewSyncKey, type ViewSyncRef } from './viewSync.js'

const OOXML = new Set(['xlsx', 'docx', 'pptx'])

const Spinner = (
  <div className="flex-1 flex items-center justify-center">
    <Loader2 className="animate-spin themed-text-muted" size={24} />
  </div>
)

export interface OfficeCliRenderProps {
  fileId: string
  /** Omit = current head (the endpoint's no-version branch). */
  versionId?: string
  fileName?: string
  viewSync?: ViewSyncRef
  /** View-sync key mode: 'readonly' for View / preview, 'edit' otherwise. */
  mode: 'readonly' | 'edit'
  /** Rendered on non-OOXML or an officecli render failure. */
  fallback: ReactNode
}

export function OfficeCliRender({ fileId, versionId, fileName, viewSync, mode, fallback }: OfficeCliRenderProps) {
  const ext = fileName?.split('.').pop()?.toLowerCase() || ''
  const eligible = OOXML.has(ext)

  const [html, setHtml] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const [loading, setLoading] = useState(eligible)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Follow Papan's theme, frozen AT LOAD (consistent with OnlyOffice, which bakes
  // uiTheme at init). isDarkRef holds the current value so the srcDoc build reads the
  // theme in force when the HTML arrives; a theme changed while the View stays open
  // takes effect on the next load / reopen. docDark themes the outer wrapper.
  const themeCtx = useThemeOptional()
  const isDark = themeCtx?.settings?.theme?.workspace !== 'light'
  const isDarkRef = useRef(isDark); isDarkRef.current = isDark
  const [docDark, setDocDark] = useState(isDark)

  useEffect(() => {
    if (!eligible) return
    let cancelled = false
    setLoading(true); setFailed(false); setHtml(null)
    const q = versionId ? `?version=${encodeURIComponent(versionId)}` : ''
    fetchRaw(`/api/files/${fileId}/officecli-preview${q}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`status ${r.status}`)
        const text = await r.text()
        if (!cancelled) { setHtml(text); setDocDark(isDarkRef.current); setLoading(false) }
      })
      .catch(() => { if (!cancelled) { setFailed(true); setLoading(false) } })
    return () => { cancelled = true }
    // Head (no versionId) fetches fresh on every mount; the live-editor View
    // remounts this component on each Edit→View entry AFTER the save-gate has
    // confirmed the head persisted, so the mount fetch always sees current bytes.
  }, [eligible, fileId, versionId])

  // The final srcDoc: officecli's HTML with the CSP, theme and the saved view state
  // (for the in-iframe restore script) baked in by the parent — no DOM reach. Built
  // once per HTML/theme/file; NOT rebuilt on a live viewSync save (that would reload
  // the frame), so `saved` is the state to RESTORE at load, read once here.
  const key = viewSyncKey(fileId, mode)
  const srcDoc = useMemo(() => {
    if (html == null) return ''
    const saved = (viewSync?.[key] || viewSync?.[`${fileId}:office`]) as OfficeCliViewState | undefined
    return buildOfficeCliSrcDoc(html, { dark: isDarkRef.current, saved })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- saved read once at load; viewSync writes must not rebuild
  }, [html, key, fileId])

  // Persist view-state changes the in-iframe augment script posts OUT. Accept the
  // message ONLY from this iframe's own window (opaque origin ⇒ event.origin is
  // "null", so identity is by source reference, never by origin string).
  useEffect(() => {
    if (!viewSync) return
    const onMessage = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) return
      const d = e.data as { type?: string; state?: OfficeCliViewState } | null
      if (d && d.type === 'pm-viewsync' && d.state) viewSync[key] = d.state as Record<string, unknown>
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [viewSync, key])

  if (!eligible || failed) return <>{fallback}</>
  if (loading) return Spinner
  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ background: docDark ? '#1e1e1e' : '#fff' }}>
      <iframe
        ref={iframeRef}
        key={versionId ?? 'head'}
        srcDoc={srcDoc}
        title={`Preview of ${fileName ?? 'document'}`}
        sandbox="allow-scripts"
        className="flex-1 w-full border-0"
      />
    </div>
  )
}
